/**
 * Shared column factories for Job tables
 *
 * Factory functions that return column definitions for consistent job table rendering.
 * Eliminates DRY violation between Jobs section and CapabilityJobStatus.
 *
 * Used by:
 * - Jobs section (main table) via createJobColumns
 * - CapabilityJobStatus (capability drawer modal)
 * - Future: RiskDrawer, AssetDrawer, AgentJobsDrawer
 *
 * @example
 * ```tsx
 * // Using individual factories
 * const columns = [
 *   jobColumnFactories.status({ animateRunning: true }),
 *   jobColumnFactories.source(),
 *   jobColumnFactories.updated(),
 * ];
 *
 * // Using helper for standard columns
 * const columns = createStandardJobColumns({ animateRunning: true });
 * ```
 */

import { DashedTag } from '@praetorian-chariot/ui';

import { NameColumn } from '@/components/NameColumn';
import { getColumnWidth } from '@/components/table/columns/registry';
import { Column } from '@/components/table/types';
import { JobStatusCell } from '@/sections/jobs/components/cells';
import { stripJobSourceTimestamp } from '@/sections/jobs/utils';
import { Job } from '@/types';
import { formatDate } from '@/utils/date.util';

import { COLUMN_ALIGNMENT, COLUMN_LABELS } from './columns';

/**
 * Options for status column factory
 */
export interface StatusColumnOptions {
  /** Whether to animate the running status icon (default: true) */
  animateRunning?: boolean;
}

/**
 * Individual column factory functions
 *
 * Each factory returns a complete column definition with:
 * - Consistent labeling from COLUMN_LABELS
 * - Width configuration from column registry
 * - Cell renderer with proper formatting
 */
export const jobColumnFactories = {
  /**
   * Status column - displays job status icon with optional animation
   *
   * @param options - Configuration options
   * @returns Column definition for status
   */
  status: (options?: StatusColumnOptions): Column<Job> => ({
    id: 'status',
    label: COLUMN_LABELS.status,
    ...getColumnWidth('job', 'status'),
    cell: job => (
      <JobStatusCell
        job={job}
        animateRunning={options?.animateRunning ?? true}
      />
    ),
    className: '[&>span]:justify-center',
    align: COLUMN_ALIGNMENT.status,
    sorting: true,
  }),

  /**
   * Source column - displays job type as dashed tag badge
   *
   * Shows stripped job source without timestamp suffix
   *
   * @returns Column definition for source/type
   */
  source: (): Column<Job> => ({
    id: 'source',
    label: COLUMN_LABELS.type,
    ...getColumnWidth('job', 'type'),
    cell: (job: Job) => (
      <DashedTag size="xs" label={stripJobSourceTimestamp(job.source)} />
    ),
    sorting: true,
  }),

  /**
   * Name column - displays source entity name
   *
   * @returns Column definition for name/source label
   */
  name: (): Column<Job> => ({
    id: 'name',
    label: COLUMN_LABELS.source,
    ...getColumnWidth('job', 'source'),
    cell: (job: Job) => job.name || '-',
    sorting: false,
  }),

  /**
   * DNS column - displays target hostname with description
   *
   * Parses target from job key format: #job#123#capability#target
   *
   * @returns Column definition for DNS/target
   */
  dns: (): Column<Job> => ({
    id: 'dns',
    label: COLUMN_LABELS.target,
    ...getColumnWidth('job', 'target'),
    cell: (job: Job) => {
      // Parse target hostname from job key (index 3)
      // job.key format: #job#123#port-scanning#example.com
      const targetHostname = job.key.split('#')[3];

      return (
        <NameColumn name={job.dns || '-'} description={targetHostname || ''} />
      );
    },
    sorting: false,
  }),

  /**
   * Created column - displays job creation timestamp
   *
   * @returns Column definition for created date
   */
  created: (): Column<Job> => ({
    id: 'created',
    label: COLUMN_LABELS.created,
    ...getColumnWidth('job', 'created'),
    cell: (job: Job) => formatDate(job.created, 'yyyy-MM-dd HH:mm', false),
    sorting: true,
  }),

  /**
   * Updated column - displays last updated timestamp
   *
   * @returns Column definition for updated date
   */
  updated: (): Column<Job> => ({
    id: 'updated',
    label: COLUMN_LABELS.updated,
    ...getColumnWidth('job', 'updated'),
    cell: (job: Job) => formatDate(job.updated, 'yyyy-MM-dd HH:mm', false),
    sorting: true,
  }),
};

/**
 * Create standard job columns array
 *
 * Returns the default set of columns used by the main Jobs table:
 * status, source, name, dns, created, updated
 *
 * @param options - Configuration options for columns
 * @returns Array of column definitions
 *
 * @example
 * ```tsx
 * const columns = createStandardJobColumns({ animateRunning: true });
 * ```
 */
export const createStandardJobColumns = (
  options?: StatusColumnOptions
): Column<Job>[] => [
  jobColumnFactories.status(options),
  jobColumnFactories.source(),
  jobColumnFactories.name(),
  jobColumnFactories.dns(),
  jobColumnFactories.created(),
  jobColumnFactories.updated(),
];
